import React, { useState } from 'react';
import { Clock, Award, Users, Mic } from 'lucide-react';
import programData from '@/data/programData.json';

const HALL_ACCENT = {
  A: '#FF6F4A',
  B: '#FFB6C1',
  C: '#4ECDC4',
  D: '#A78BFA',
};

const SessionCard = ({ session, accent }) => {
  const title = session.title || (session.highlights[0] && session.highlights[0].label) || 'Session';
  return (
    <div className="glass-card p-5 md:p-6" data-testid="program-session-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <span
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap self-start"
          style={{ background: `${accent}22`, color: accent }}
        >
          <Clock className="h-3.5 w-3.5" /> {session.time || 'TBA'}
        </span>
        <h4 className="text-base md:text-lg font-bold text-white leading-snug">{title}</h4>
      </div>

      {session.faculty.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {session.faculty.map((f, i) => (
            <span key={i} className="text-[11px] px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-slate-300">
              <span className="text-slate-400">{f.role}:</span> <span className="text-white font-medium">{f.name}</span>
            </span>
          ))}
        </div>
      )}

      {session.highlights.map((h, i) => (
        <div key={i} className="flex items-start gap-3 mb-3 p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
          <Award className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-yellow-300">{h.label}{h.duration ? ` · ${h.duration}` : ''}</p>
            {h.detail && <p className="text-xs text-slate-300 mt-0.5">{h.detail}</p>}
          </div>
        </div>
      ))}

      {session.talks.length > 0 && (
        <ul className="divide-y divide-white/5">
          {session.talks.map((t, i) => (
            <li key={i} className="flex items-start gap-3 py-2.5">
              <Mic className="h-3.5 w-3.5 text-slate-500 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white leading-snug">{t.topic}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t.speaker}</p>
              </div>
              {t.duration && (
                <span className="text-[10px] text-slate-400 whitespace-nowrap pt-0.5">{t.duration}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {session.notes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {session.notes.map((n, i) => (
            <span key={i} className="text-[11px] italic text-slate-400 flex items-center gap-1">
              <Users className="h-3 w-3" /> {n}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ProgramSchedule = () => {
  const days = programData.days || [];
  const [dayIdx, setDayIdx] = useState(0);
  const activeDay = days[dayIdx] || { halls: [] };
  const [hallId, setHallId] = useState((activeDay.halls[0] || {}).id || 'A');

  const activeHall = activeDay.halls.find((h) => h.id === hallId) || activeDay.halls[0];

  const changeDay = (i) => {
    setDayIdx(i);
    const d = days[i];
    const exists = d.halls.find((h) => h.id === hallId);
    if (!exists && d.halls[0]) setHallId(d.halls[0].id);
  };

  if (days.length === 0) return null;

  return (
    <div className="mb-12" data-testid="program-schedule">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2">
          Scientific <span className="gradient-text">Programme</span>
        </h2>
        <p className="text-slate-300 text-sm">Browse sessions by day and hall</p>
      </div>

      {/* Day tabs */}
      <div className="flex flex-wrap justify-center gap-3 mb-5">
        {days.map((d, i) => (
          <button
            key={d.key}
            onClick={() => changeDay(i)}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
              i === dayIdx ? 'bg-[#FF6F4A] text-white' : 'glass-card text-slate-300 hover:text-white'
            }`}
            data-testid={`program-day-tab-${i}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Hall tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {activeDay.halls.map((h) => {
          const accent = HALL_ACCENT[h.id] || '#FF6F4A';
          const active = h.id === hallId;
          return (
            <button
              key={h.id}
              onClick={() => setHallId(h.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                active ? 'text-white' : 'text-slate-300 border-white/10 bg-white/5 hover:bg-white/10'
              }`}
              style={active ? { background: accent, borderColor: accent } : {}}
              data-testid={`program-hall-tab-${h.id}`}
            >
              {h.name}{h.venue ? ` · ${h.venue}` : ''}
            </button>
          );
        })}
      </div>

      {/* Sessions */}
      {activeHall ? (
        <div className="space-y-5 max-w-4xl mx-auto" data-testid={`program-hall-${hallId}`}>
          {activeHall.sessions.map((s, i) => (
            <SessionCard key={i} session={s} accent={HALL_ACCENT[activeHall.id] || '#FF6F4A'} />
          ))}
        </div>
      ) : (
        <p className="text-center text-slate-400">No sessions for this hall.</p>
      )}
    </div>
  );
};

export default ProgramSchedule;
